import React, { ForwardedRef, ReactNode } from 'react';
import clsx from 'clsx';
import { AriaModalOverlayProps } from '@react-aria/overlays';
import { AriaDialogProps } from '@react-types/dialog';
import styles from '@danske/sapphire-css/components/dialog/dialog.module.css';

import { Modal } from '../../Modal';
import { ModalLayoutContext } from '../../ModalLayout';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export type DialogProps = AriaModalOverlayProps &
  AriaDialogProps &
  SapphireStyleProps &
  GlobalDomAttributes & {
    children: ReactNode;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    /**
     * Whether the dialog is currently open.
     */
    isOpen: boolean;

    /** Handler that is called when the dialog should close. */
    onClose?: () => void;

    /**
     * Callback for when the modal exit transition is done and not visible
     * anymore. This happens a short while after the `isOpen` state is set to
     * `false`.
     */
    onCloseTransitionDone?: () => void;

    /**
     * Whether to close the dialog when the backdrop is clicked/touched.
     * @default false
     */
    isDismissable?: boolean;
    /**
     * Whether pressing the escape key to close the dialog should be disabled.
     * @default false
     */
    isKeyboardDismissDisabled?: boolean;

    /**
     * Whether the dialog has a default padding or not. This is necessary only
     * if not using the `ModalLayout` inside the dialog.
     */
    withPadding?: boolean;

    /** @deprecated Prop is not applicable to dialogs and was included by mistake and will be removed in a future major version. */
    shouldCloseOnBlur?: boolean;

    /** @deprecated Prop is not applicable to dialogs and was included by mistake and will be removed in a future major version. */
    shouldCloseOnInteractOutside?: (element: Element) => boolean;
  };

export const Dialog = React.forwardRef(function DialogWrapper(
  props: DialogProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const { children, onClose, size = 'sm' } = props;

  const { styleProps } = useSapphireStyleProps(props);

  return (
    <Modal {...props} isOpen={!!props.isOpen} ref={ref}>
      {({ titleProps, transitionState }) => (
        <div
          className={clsx(
            styleProps.className,
            styles['sapphire-dialog'],
            styles['sapphire-dialog'],
            styles[`sapphire-dialog--${size}` as const],
            {
              [styles['sapphire-dialog--exiting']]: [
                'exiting',
                'exited',
              ].includes(transitionState),
              [styles['sapphire-dialog--padded']]: props.withPadding,
            }
          )}
          style={styleProps.style}
        >
          <ModalLayoutContext.Provider
            value={{
              onClose,
              titleProps,
            }}
          >
            {children}
          </ModalLayoutContext.Provider>
        </div>
      )}
    </Modal>
  );
});
